import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Crown, 
  Heart, 
  Shield, 
  Star, 
  Users, 
  Clock, 
  Award,
  Sparkles,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const OurDifferencePage = () => {
  const differentiators = [
    {
      icon: Crown,
      title: "Sélection Ultra-Exclusive",
      subtitle: "Moins de 1% des propriétés",
      description: "Chaque villa PRIVIA est choisie selon des critères d'exception : architecture remarquable, emplacement privilégié, et services cinq étoiles. Notre processus de sélection rigoureux garantit une collection unique au monde.",
      stats: "200+ villas sélectionnées sur 25,000 visitées"
    },
    {
      icon: Heart,
      title: "Personnalisation Émotionnelle",
      subtitle: "Voyage sur-mesure selon vos émotions",
      description: "PRIVIA révolutionne le luxe en associant chaque destination à une émotion. Sérénité, Privilège, Anticipation... Nous créons l'expérience qui correspond parfaitement à votre état d'esprit.",
      stats: "5 univers émotionnels uniques"
    },
    {
      icon: Users,
      title: "Concierge Ultra-Personnel",
      subtitle: "Un expert dédié par villa",
      description: "Chaque propriétaire de villa dispose d'un concierge personnel formé aux plus hauts standards du service luxury. Anticipation, discrétion et expertise locale garanties 24h/24.",
      stats: "1 concierge dédié par villa"
    },
    {
      icon: Shield,
      title: "Garantie Satisfaction Absolue",
      subtitle: "Promesse de perfection",
      description: "Si votre expérience ne correspond pas à vos attentes, nous nous engageons à tout rectifier immédiatement ou à vous rembourser intégralement. Votre satisfaction est notre seule mesure de succès.",
      stats: "98.7% de satisfaction client"
    },
    {
      icon: Sparkles,
      title: "Expériences Impossibles",
      subtitle: "Accès aux expériences privées",
      description: "Dîner avec un chef étoilé Michelin dans votre villa, concert privé au coucher du soleil, accès exclusif à des lieux fermés au public... PRIVIA ouvre les portes de l'impossible.",
      stats: "500+ expériences exclusives"
    },
    {
      icon: Clock,
      title: "Service Anticipé",
      subtitle: "Vos besoins prévus avant même de les exprimer",
      description: "Notre intelligence artificielle et nos concierges analysent vos préférences pour anticiper vos besoins. Votre champagne préféré vous attend, vos restaurants favoris sont réservés avant même votre demande.",
      stats: "Anticipation de 95% des demandes"
    }
  ];

  const testimonialQuotes = [
    {
      quote: "PRIVIA ne loue pas des villas, ils offrent des clés pour des univers parallèles où tout devient possible.",
      author: "Isabella D., Collectionneuse d'Art",
      location: "Villa Majesté, Côte d'Azur"
    },
    {
      quote: "J'ai voyagé dans le monde entier. PRIVIA m'a fait découvrir des émotions que je ne connaissais pas.",
      author: "Marcus L., Entrepreneur",
      location: "Villa Sérénité, Saint-Barthélemy"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Crown className="w-4 h-4 mr-2" />
            Notre ADN
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Ce qui nous rend Uniques
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            PRIVIA réinvente le luxe en créant des expériences impossibles ailleurs. 
            Découvrez les six piliers de notre différence.
          </p>
        </div>
      </section>

      {/* Core Differentiators */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Six Piliers d'Exception
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Chaque aspect de l'expérience PRIVIA est pensé pour dépasser vos attentes les plus élevées
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {differentiators.map((diff, index) => {
              const IconComponent = diff.icon;
              return (
                <Card key={index} className="p-8 h-full hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 
                        className="text-2xl font-light text-gray-800 mb-2"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {diff.title}
                      </h3>
                      
                      <p 
                        className="text-amber-600 font-medium mb-4 text-sm uppercase tracking-wide"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {diff.subtitle}
                      </p>
                      
                      <p 
                        className="text-gray-600 leading-relaxed mb-4"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {diff.description}
                      </p>
                      
                      <div className="flex items-center gap-2 text-sm text-amber-600 font-medium">
                        <CheckCircle className="w-4 h-4" />
                        <span>{diff.stats}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Notre Processus d'Excellence
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Comment nous garantissons une expérience parfaite à chaque séjour
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-light text-white">1</span>
              </div>
              <h3 className="text-xl font-medium mb-4">Analyse Émotionnelle</h3>
              <p className="text-gray-300 leading-relaxed">
                Nous identifions l'émotion que vous recherchez pour orienter parfaitement votre expérience
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-light text-white">2</span>
              </div>
              <h3 className="text-xl font-medium mb-4">Curation Personnalisée</h3>
              <p className="text-gray-300 leading-relaxed">
                Notre équipe sélectionne la villa et les expériences qui correspondent exactement à vos attentes
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-light text-white">3</span>
              </div>
              <h3 className="text-xl font-medium mb-4">Exécution Parfaite</h3>
              <p className="text-gray-300 leading-relaxed">
                Chaque détail est orchestré par votre concierge dédié pour une expérience sans faille
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Ils ont Vécu la Différence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {testimonialQuotes.map((testimonial, index) => (
              <Card key={index} className="p-8 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
                <div className="mb-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-500" fill="currentColor" />
                    ))}
                  </div>
                  <p 
                    className="text-gray-700 text-lg italic leading-relaxed mb-6"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    "{testimonial.quote}"
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{testimonial.author}</p>
                  <p className="text-sm text-amber-600">{testimonial.location}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Vivez la Différence PRIVIA
          </h2>
          <p 
            className="text-xl mb-8 opacity-90 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Découvrez pourquoi nos clients ne peuvent plus voyager autrement après avoir vécu l'expérience PRIVIA
          </p>
          <Button 
            size="lg"
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-amber-700 px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Découvrir nos Villas
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OurDifferencePage;